// NPM MODULES
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

// REQUEST
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

// MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// UTILS
import { validateName, validatePhoneNumber } from '../../../../utils/validate';

// COMPONENTS
import StagedModal from '../../../common/StagedModal';

const ContactAdd = (props) => {
  const [deleteContact, setDeleteContact] = useState(false);
  const [smsContact, setSmsContact] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [edits, setEdits] = useState({
    contact_first_name: '',
    contact_last_name: '',
    contact_name: '',
    contact_type: 'personal',
    contact_email: '',
    contact_tn: '',
  });
  const [showStaged, setShowStaged] = useState(false);
  const [modalParameters, setModalParameters] = useState({});
  const [stage, setStage] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [refresh, setRefresh] = useState(false);
  const { createNewSmsContact, upgradeContactToGlobal } =
    useAddressBookRequest();

  /**
   * Convenience function that renders checkbox for contact type.
   */
  const generateContactType = () => {
    const { accessLevel, accessType } = props;

    return (
      <FormControl component="fieldset">
        <FormLabel component="legend">
          {AddressBookFormattedMessages.contactType}
        </FormLabel>
        <RadioGroup row aria-label="contactType" name="contact_type">
          <FormControlLabel
            value="personal"
            control={<Radio name="contactType" />}
            label={AddressBookFormattedMessages.personal}
            color="secondary"
          />
          <FormControlLabel
            value="global"
            control={<Radio name="contactType" />}
            label={AddressBookFormattedMessages.global}
            disabled={accessLevel > '0' && accessType !== '0' ? false : true}
            color="secondary"
          />
        </RadioGroup>
      </FormControl>
    );
  };

  /*
    Handles state changes from the Form
  */
  const handleChangeEvent = (event) => {
    let { name, value } = event.target;

    const formData = { ...edits }; // Assuming 'values' is your form state object

    if (name === 'email') {
      value = value.toLowerCase();
    }
    formData[name] = value;

    // Handle display name logic
    if (name === 'contact_first_name' || name === 'contact_last_name') {
      // Only update display name if it was previously auto-generated
      const wasAutoGenerated =
        edits.contact_name ===
        `${edits.contact_first_name} ${edits.contact_last_name}`.trim();

      if (wasAutoGenerated || !edits.contact_name) {
        // Generate new display name if both first and last name exist
        if (formData.contact_first_name || formData.contact_last_name) {
          formData.contact_name = `${formData.contact_first_name || ''} ${
            formData.contact_last_name || ''
          }`.trim();
        }
      }
    } else if (name === 'contact_name') {
      // Direct update of display name by user input
      formData.contact_name = value;
    }
    if (name === 'email') {
      value = value.toLowerCase();
    }

    setEdits(formData);
  };

  const errorHandler = (error) => {
    setError(error.error);
  };

  const handleSubmit = async () => {
    // If not already saving...

    // Needs number
    setRefresh(false);
    setError('');

    if (!edits.contact_type) {
      return setError('Contact Type is required');
    }
    if (!edits.contact_tn) {
      return setError('Phone Number is required');
    }
    // If number isn't only digits

    if (!validatePhoneNumber(edits.contact_tn)) {
      return setError(
        'Incorrect contact phone format, should only contain numbers and "+", eg: +0123456789/0123456789'
      );
    }
    // Needs entry_name
    if (!validateName(edits.contact_name)) {
      return setError('Display Name is required');
    }

    setIsSaving(true);
    let response = await createNewSmsContact(edits, errorHandler);

    if (response?.error) {
      setIsSaving(false);
      setError(response.error);
      return;
    }

    if (response?.prompt) {
      setPrompt(response.prompt);
      setEdits(response.contact);
      await handleContactUpgrade(edits, response.prompt);
    } else if (!response?.error) {
      props.callBack(true);
      setEdits({
        contact_first_name: '',
        contact_last_name: '',
        contact_name: '',
        contact_type: 'personal',
        contact_email: '',
        contact_tn: '',
      });
    }
    setIsSaving(false);
  };

  const createSmsContactWithoutUpgrading = async (body) => {
    if (stage === 0) {
      setStage(1);
      setModalParameters({
        stageTitle: 'Upgrade Notification',
        stagePending: `Creating the ${body.contact_type} contact...`,
        stageSuccess: `The ${body.contact_type} contact created successfully!`,
        stageFailure: `The ${body.contact_type} contact failed to be created!`,
      });
      let result = await upgradeContactToGlobal(body, errorHandler);
      if (!result.error) {
        setStage(2);
        setRefresh(true);
      } else {
        setStage(3);
      }
    }
  };

  const handleContactUpgrade = async (body, prompt) => {
    switch (prompt) {
      case 'UPGRADE_TO_GLOBAL':
        if (!showStaged) {
          setShowStaged(true);
          setStage(0);
          setModalParameters({
            stageTitle: 'Upgrade Notification',
            stageConfirmation: `Personal contact(s) with this number exists. Would you like to upgrade it to global?\n
                                  Please press No if you want to continue creating a global contact without upgrading.`,
            stagePending: 'Upgrading contact to global...',
            stageSuccess: 'Contact successfully upgraded to global!',
            stageFailure:
              'Failed to upgrade contact to global. Please try again.',
          });
        } else if (stage === 0) {
          setStage(1);
          body.prompt = 'upgrade';
          let result = await upgradeContactToGlobal(body, errorHandler);
          if (!result.error) {
            setStage(2);
            setRefresh(true);
          } else {
            setStage(3);
          }
        }
        break;
      case 'UPGRADE_MULTIPLE_PERSONAL_CONTACTS':
        if (!showStaged) {
          setShowStaged(true);
          setStage(0);
          setModalParameters({
            stageTitle: 'Upgrade Notification',
            stageConfirmation: `Personal contacts with this number exist across different users.
                                  Would you like to upgrade all of them to a single global contact?
                                  (Note: This will affect other users' address books and the linked distribution list(s).)\n
                                  Please press No if you want to continue creating a personal contact without upgrading.`,
            stagePending: 'Upgrading multiple contacts to global...',
            stageSuccess:
              'All contacts successfully merged and upgraded to global!',
            stageFailure:
              'Failed to upgrade contacts to global. Please try again.',
          });
        } else if (stage === 0) {
          setStage(1);
          body.prompt = 'upgrade';
          body.contact_type = 'global';
          let result = await upgradeContactToGlobal(body, errorHandler);
          if (!result.error) {
            setStage(2);
            setRefresh(true);
          } else {
            setStage(3);
          }
        }
        break;
      default:
        console.error('Attempted to perform an unknown action', prompt, stage);
        break;
    }
  };

  const removeStaged = async () => {
    setShowStaged(false);
    setModalParameters({});
    setRefresh(false);
  };

  const generateUpgradeModal = () => {
    return (
      <StagedModal
        showStaged={showStaged}
        removeStaged={
          refresh ? () => props.callBack(true) : () => removeStaged()
        }
        removeStagedSlow={() => setStage(0)}
        stage={stage}
        performAction={() => handleContactUpgrade(edits, prompt)}
        performNoAction={() => createSmsContactWithoutUpgrading(edits)}
        stageTitle={modalParameters?.stageTitle}
        stageConfirmation={modalParameters?.stageConfirmation}
        stagePending={modalParameters?.stagePending}
        stageSuccess={modalParameters?.stageSuccess}
        stageFailure={modalParameters?.stageFailure}
      />
    );
  };

  const generateButtonGroup = () => {
    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Stack sx={{ flexDirection: 'row', gap: 2, justifyContent: 'end' }}>
            <Button color="secondary" variant="link" onClick={props.callBack}>
              {AddressBookFormattedMessages.cancelText}
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              color="secondary"
              disabled={
                edits.contact_name === '' ||
                edits.contact_tn === '' ||
                edits.contact_type === '' ||
                isSaving
              }
            >
              {isSaving
                ? AddressBookFormattedMessages.saving
                : AddressBookFormattedMessages.save}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  /*
    Handles any errors generated
  */
  const generateError = () => {
    if (error) {
      return (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        </Grid>
      );
    }
    // Return no error if none present
    return null;
  };

  return (
    <Box>
      <Box component="form">
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl component="fieldset">
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <FormLabel sx={{ minWidth: 150 }} required>
                  {AddressBookFormattedMessages.contactType}
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="contactType"
                  name="contact_type"
                  value={edits.contact_type}
                  onChange={handleChangeEvent}
                >
                  <FormControlLabel
                    value="personal"
                    control={<Radio color="secondary" />}
                    label={AddressBookFormattedMessages.personal}
                  />
                  <FormControlLabel
                    value="global"
                    control={<Radio color="secondary" />}
                    label={AddressBookFormattedMessages.global}
                    disabled={
                      props.accessLevel > '0' && props.accessType !== '0'
                        ? false
                        : true
                    }
                  />
                </RadioGroup>
              </Stack>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }} required>
                {AddressBookFormattedMessages.displayName}
              </InputLabel>

              <TextField
                name="contact_name"
                value={edits.contact_name}
                onChange={handleChangeEvent}
                fullWidth
                required
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }}>
                {AddressBookFormattedMessages.emailText}
              </InputLabel>

              <TextField
                name="contact_email"
                value={edits.contact_email}
                onChange={handleChangeEvent}
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 150 }} required id="contact_tn_label">
                {AddressBookFormattedMessages.phoneText}
              </InputLabel>

              <TextField
                labeId="contact_tn_label"
                name="contact_tn"
                value={edits.contact_tn}
                onChange={handleChangeEvent}
                required
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>{generateError()}</Grid>
          <Grid size={12}>{generateButtonGroup()}</Grid>
        </Grid>
      </Box>
      {generateUpgradeModal()}
    </Box>
  );
};

export default ContactAdd;
